const en = {
  nav: {
    products: "Products",
    stories: "Stories",
    about: "About",
  },
  hero: {
    eyebrow: "One connected career toolkit",
    title: "From first search to final interview.",
    description:
      "Jobolar brings job discovery, application documents, email and browser application tools, and live interview support into one clear workflow.",
    primaryCta: "Explore all products",
    secondaryCta: "Start the free trial",
    trustLine: "Five connected products, led by the screen-share-invisible Desktop Interview Assistant for supported setups. You stay in control.",
    workspaceEyebrow: "Your Jobolar workspace",
    workspaceTitle: "One journey, five connected products",
    liveLabel: "Available now",
    desktopFlagshipLabel: "Flagship · Invisible Desktop",
    metricProducts: "5 product areas",
    metricCountries: "49 Email Auto Apply countries",
    metricSurfaces: "Web · Chrome · Desktop",
  },
  homeProducts: {
    eyebrow: "The Jobolar platform",
    title: "Support for every stage of your job search",
    description:
      "Choose the tools you need now and keep the same profile, résumés, preferences, and activity history as your search develops.",
    detailsCta: "See how it works",
    allProductsCta: "Compare all products",
  },
  homeShowcase: {
    eyebrow: "Inside the Jobolar workspace",
    title: "See the connected workflow in action",
    description:
      "Explore the real product surfaces behind application analytics, Chrome Smart Apply, résumé matching, and Interview Assistant setup.",
    ariaLabel: "Choose a Jobolar product view",
    defaultLabel: "Product view",
  },
  how: {
    eyebrow: "How Jobolar works",
    title: "A practical path from discovery to interview",
    description:
      "Start with your profile and preferences, then use each product where it adds value. Availability and automation depend on your plan, device, country, job site, and posting.",
    steps: [
      {
        title: "Set your direction",
        description: "Add your target roles, locations, preferences, and one or more résumé profiles.",
      },
      {
        title: "Discover matching jobs",
        description: "Jobolar searches daily and sends curated local and international opportunities with direct links.",
      },
      {
        title: "Prepare the right documents",
        description: "Use your Base Resume or, on Pro, Smart Tailor or Deep Tailor with job-specific cover letters.",
      },
      {
        title: "Apply through the right channel",
        description: "Use eligible Email Auto Apply or complete supported browser applications with the Chrome extension.",
      },
      {
        title: "Prepare and perform",
        description: "Practice with Mock Interview, or use the flagship Desktop overlay for private real-time guidance in supported screen-sharing setups.",
      },
    ],
    privacyTitle: "Purpose-based access",
    privacyDescription: "Gmail permission is used only for Email Auto Apply. Browser and microphone permissions are requested only when their features need them.",
    controlTitle: "Review and control",
    controlDescription: "Pause, resume, cancel, review before submit, and choose the résumé mode allowed by your plan.",
    realityTitle: "Clear eligibility",
    realityDescription: "A supported country or site does not mean every job can be automated. Jobolar explains when manual action is required.",
  },
  features: {
    eyebrow: "Built around real job-search work",
    title: "Powerful automation without hiding the boundaries",
    description: "Jobolar combines deterministic tools, focused AI, privacy controls, and transparent handoffs when a person must finish the task.",
    items: [
      { title: "Local and international discovery", description: "Daily matching, curated opportunities, unlimited alerts, direct links, and analytics." },
      { title: "Two application channels", description: "Eligible email submissions and supported browser form completion are explained separately." },
      { title: "Three résumé modes", description: "Base Resume, Smart Tailor, and Career Profile-powered Deep Tailor keep source truth and tailoring distinct." },
      { title: "Deterministic and AI assistance", description: "Use unlimited deterministic Copilot Fill, then Generate or Refine answers with AI when needed." },
      { title: "Invisible Desktop Interview Assistant", description: "Private overlay guidance is designed to stay off supported screen shares, with transcription, talking points, answer structures, and follow-ups." },
      { title: "Privacy and user control", description: "Purpose-scoped connections, review controls, session controls, and clear deletion and retention explanations." },
    ],
  },
  about: {
    eyebrow: "Why Jobolar exists",
    title: "Career technology should help more people move forward.",
    lead:
      "Job searches are already demanding. Useful tools should reduce repetitive work and make high-quality support accessible across regions—not add another confusing expense.",
    affordabilityTitle: "Affordability with a purpose",
    affordabilityDescription:
      "Jobolar keeps pricing accessible while paying for the AI models, secure infrastructure, email delivery, document processing, and support that power the service.",
    peopleTitle: "Help before hype",
    peopleDescription:
      "We focus on practical outcomes: better discovery, clearer documents, faster applications, stronger preparation, and honest explanations when automation cannot complete a job.",
    responsibilityTitle: "Responsible by design",
    responsibilityDescription:
      "Users stay responsible for the accuracy of their information and final submissions. Jobolar does not promise interviews, offers, or universal automation.",
    cta: "See plans and access",
  },
  productPage: {
    eyebrow: "Jobolar products",
    title: "Five products, one connected job-search workflow",
    description:
      "Products describe what Jobolar does. Subscription plans determine which capabilities, limits, résumé modes, and devices you can use.",
    availability: "Availability",
    planAccess: "Plan access",
    deviceAccess: "Where you use it",
    capabilities: "What it includes",
    howTitle: "How it works",
    importantTitle: "Good to know",
    startCta: "Get started",
    openCta: "Open this product",
    installTitle: "Install and connect",
    installDescription: "Approved Jobolar listings will replace these marketplace links automatically through public environment variables.",
    browserStore: "Open Chrome Web Store",
    windowsStore: "Open Microsoft Store",
    macStore: "Open macOS page",
    webApp: "Open Jobolar Web",
    currentProduct: "Current product",
    visualEyebrow: "Product experience",
    visualTitle: "A closer look at the workflow",
    visualProductLabel: "Jobolar product view",
    mockShowcaseEyebrow: "AI Mock Interview",
    mockShowcaseTitle: "Practice the conversation before it counts",
    mockShowcaseDescription: "Rehearse with an AI interviewer, answer adaptive follow-up questions, and review private coaching before a real interview.",
    mockShowcaseFeatures: ["Adaptive AI follow-up questions", "On-device visual delivery coaching", "Key points, answer guidance, insights, and session history"],
    mockShowcaseCta: "Start a mock interview",
    mockShowcaseAlt: "Jobolar AI Mock Interview with a fictional Black professional using visual delivery coaching",
    chromeFocusEyebrow: "Chrome Interview Assistant · New",
    chromeFocusTitle: "See the interviewer and your coaching in one adjustable view",
    chromeFocusDescription: "Place a translucent coaching window over supported browser interviews, move and resize it around the conversation, and tune the background so the interviewer stays visible behind Jobolar guidance.",
    chromeFocusFeatures: [
      "Adjustable background transparency",
      "Key Points, Full Answer, Interview Insights, and Follow-up Questions",
      "Manual Listen controlled with the Space key",
    ],
    chromeFocusCta: "Set up Chrome Interview Assistant",
    chromeFocusAlt: "Jobolar Chrome Interview Assistant Translucent Focus Overlay with guidance over a visible synthetic male interviewer",
    chromeFocusCaution: "Chrome Focus Overlay is a visible browser window and may appear in screen shares or recordings. It is not invisible like the supported Desktop experience. Test the exact sharing setup before every interview.",
    visualLabels: {
      ariaLabel: "Choose a product screen",
      defaultLabel: "Product screen",
      dashboard: "Analytics dashboard",
      autoApply: "Auto Apply",
      profile: "Profile",
      resume: "Résumé",
      copilot: "Copilot",
      interviewWeb: "Web Interview Assistant",
      interviewChrome: "Chrome Focus Overlay",
      interviewDesktop: "Invisible Desktop Assistant",
      mockInterview: "AI Mock Interview",
    },
  },
  products: {
    "ai-job-search": {
      name: "AI Job Search & Matching",
      short: "Discover relevant local and international opportunities matched to your goals.",
      description:
        "Jobolar searches every day using your profile, target roles, locations, and preferences, then organizes useful opportunities so you can decide what to pursue.",
      availability: "Available across Jobolar’s main plans. Results depend on supported sources and your selected locations.",
      plans: "Free trial, Basic, Standard, and Pro",
      devices: "Jobolar Web, email alerts, and your analytics dashboard",
      capabilities: [
        "Daily AI-powered job searching and matching",
        "Curated local and international opportunities",
        "Direct job links delivered by email",
        "Unlimited job alerts",
        "Job-search and application analytics",
      ],
      steps: [
        { title: "Complete your profile", description: "Choose target roles, locations, and the preferences that shape your search." },
        { title: "Let Jobolar search", description: "Daily matching reviews supported sources for opportunities aligned with your goals." },
        { title: "Review and act", description: "Open direct links, track activity, or continue with an eligible Jobolar application method." },
      ],
      important: "Job matching recommends opportunities; it does not guarantee eligibility, interviews, or employment. Always review the employer’s requirements.",
      href: "/signup",
    },
    "email-auto-apply": {
      name: "Scheduled Email Auto Apply",
      short: "Send eligible applications when a posting provides a valid public application email.",
      description:
        "Jobolar can send an application from your connected Gmail account using the résumé mode allowed by your plan and the required AI-generated cover letter.",
      availability: "Eligible postings in 49 supported countries. A posting must expose a valid public employer or recruiter email.",
      plans: "Standard uses the unchanged Base Resume. Pro can use Base, Smart Tailor, or Deep Tailor.",
      devices: "Configured in Jobolar Web; applications and summaries are delivered through Gmail",
      capabilities: [
        "Scheduled applications sent on your behalf",
        "Base or AI-tailored résumé selection by plan",
        "Required job-specific AI cover letter",
        "Preference, pacing, pause, and schedule controls",
        "Manual or browser-application notifications when email is not eligible",
      ],
      steps: [
        { title: "Connect Gmail when you want email sending", description: "Grant the limited Gmail Send permission; Jobolar does not read your inbox with this scope." },
        { title: "Choose preferences and résumé mode", description: "Set your targets, schedule, and the résumé option available on your plan." },
        { title: "Send eligible applications", description: "Jobolar sends only where a valid public application email exists and reports jobs requiring another method." },
      ],
      important: "Country support never means every posting can be emailed. Employer systems, delivery, replies, interviews, and outcomes are outside Jobolar’s control.",
      href: "/onboarding?tab=gmailConsent",
    },
    "chrome-extension": {
      name: "Jobolar Chrome Extension",
      short: "Complete supported job applications directly in your browser.",
      description:
        "The extension combines deterministic Copilot Fill, a browser-local Smart Form, saved answers, AI drafting, review controls, and supported scheduled automation.",
      availability: "Chrome-based browsers and supported job sites. Fields, workflows, and automatic submission vary by site and posting.",
      plans: "Basic: deterministic Copilot Fill. Standard and Pro: Smart Apply and eligible automation features.",
      devices: "Google Chrome and compatible Chromium browser environments where supported",
      capabilities: [
        "Unlimited deterministic Copilot Fill",
        "Matching field completion and browser-local Smart Form",
        "Saved application answers",
        "Generate or Refine answers with AI",
        "Smart Apply, Review Before Submit, supported auto-submission, scheduling, pause, resume, and cancel",
      ],
      steps: [
        { title: "Install the extension", description: "Use the Chrome Web Store link, then pin Jobolar for quick access." },
        { title: "Connect your Jobolar account", description: "Sign in and approve only the connection required for the extension’s features." },
        { title: "Open a supported application", description: "Fill deterministically, review Smart Form answers, or use plan-eligible Smart Apply controls." },
      ],
      important: "Review every answer before submission. Sensitive declarations, signatures, unsupported fields, CAPTCHAs, and site changes may require manual action.",
      href: "/smart-apply/onboarding",
      install: "chrome",
    },
    "resume-cover-letters": {
      name: "AI Résumé Tailoring & Cover Letters",
      short: "Prepare truthful, job-specific application documents without losing your source résumé.",
      description:
        "Build or upload a Base Resume, match it to a job, and choose the level of tailoring your plan permits. Jobolar also creates the cover letter required for automated applications.",
      availability: "Résumé setup and document tools are available worldwide; AI modes and usage allowances depend on your plan.",
      plans: "Standard automates Base Resume only. Pro unlocks Base, Smart Tailor, and Deep Tailor.",
      devices: "Jobolar Web, with selected documents used by Email Auto Apply and the Chrome extension",
      capabilities: [
        "Base Resume with unchanged source content",
        "Smart Tailor using information already in the résumé",
        "Deep Tailor using a completed and verified Career Profile",
        "Deterministic résumé-to-job matching and AI previews",
        "Job-specific résumé tailoring and AI-generated cover letters",
      ],
      steps: [
        { title: "Create your Base Resume", description: "Upload a résumé or build structured, truthful source content in Jobolar." },
        { title: "Choose a mode", description: "Keep the Base unchanged or, on Pro, select Smart Tailor or Career Profile-powered Deep Tailor." },
        { title: "Review the output", description: "Preview, correct, and approve job-specific documents before they are used or downloaded." },
      ],
      important: "AI output can be incomplete or inaccurate. Never add experience or qualifications you do not have, and review every document before use.",
      href: "/resume-builder",
    },
    "interview-copilot": {
      name: "AI Interview Assistant",
      short: "Lead with invisible Desktop guidance, add Chrome’s translucent Focus Overlay, or use Web and mock practice.",
      description:
        "Use Jobolar Desktop for the flagship private overlay in supported screen-sharing setups. In Chrome, place a visible translucent coaching window over browser interviews while keeping the interviewer visible behind it, or use Web for live and mock interview support.",
      desktopSpotlight: {
        eyebrow: "Jobolar Desktop · Flagship interview experience",
        title: "Private guidance designed to stay off supported screen shares.",
        description:
          "Jobolar Desktop layers live transcription, talking points, and suggested-answer starts directly over the interviewer video in your private view. In supported setups, the shared view stays clean.",
        points: [
          "Overlay guidance directly on the interviewer video",
          "Click-through mode lets you interact with the app underneath",
          "Keep supported shared views free of Jobolar guidance",
          "Use compatible Windows and macOS setups",
        ],
        cta: "Explore Desktop Interview Assistant",
        imageAlt:
          "Illustration comparing a private Jobolar Desktop interview view with AI guidance over the interviewer video against a clean shared-screen view.",
        imageCaption:
          "Illustrative interface. Run Share Test before every interview; privacy depends on the meeting app, operating system, and sharing mode and is not guaranteed.",
      },
      availability: "Web and Chrome access depend on plan capabilities. Desktop’s private screen-sharing experience is available where supported and is compatibility-dependent.",
      plans: "Included allowances vary by Basic, Standard, and Pro; also available as an Interview Assistant-only subscription.",
      devices: "Jobolar Web, the Jobolar Chrome extension, and Jobolar Desktop for Windows and macOS",
      capabilities: [
        "Invisible Desktop Interview Assistant with private guidance designed to stay off supported screen shares",
        "Live Interview Assistant and Mock Interview practice",
        "Desktop click-through mode keeps guidance visible while you click the app underneath",
        "Draggable, resizable Chrome Translucent Focus Overlay with adjustable transparency",
        "Manual Listen and plan-eligible Smart Listen",
        "Live transcription, talking points, suggested and sample answers",
        "Answer structures, interview insights, and possible follow-up questions",
        "Text and microphone input, live waveform, and session controls",
      ],
      steps: [
        { title: "Prepare before the call", description: "Choose your résumé, target role, language, input method, and test microphone permissions." },
        { title: "Choose your surface", description: "Use the flagship Windows/macOS Desktop app, Chrome’s translucent Focus Overlay, or Jobolar Web." },
        { title: "Adapt the guidance", description: "Listen to the interviewer, review Jobolar’s private prompts, and answer naturally in your own voice." },
      ],
      important: "Desktop is designed to remain private in supported screen-sharing situations, but concealment is not guaranteed. Chrome Focus Overlay is a visible browser window and may appear in shares or recordings. Test your exact setup before every interview.",
      href: "/interview",
      install: "interview",
    },
  },
  install: {
    chromeTitle: "Install the Chrome extension",
    chromeDescription: "Open the Chrome Web Store, install Jobolar, pin it, then connect the same Jobolar account you use on the web.",
    chromeSteps: ["Open the marketplace listing", "Choose Add to Chrome", "Pin Jobolar from the Extensions menu", "Open a supported job application and connect"],
    interviewTitle: "Choose where Interview Assistant runs",
    interviewDescription: "Desktop is the flagship private overlay. Chrome adds a visible Translucent Focus Overlay over browser interviews. Web opens instantly with no installation.",
    web: "Web — no installation",
    chrome: "Chrome extension",
    windows: "Windows Desktop",
    mac: "macOS Desktop",
    pendingNote: "Until Jobolar’s approved listing URL is configured, the button opens the official marketplace homepage.",
    desktopSetupEyebrow: "Desktop account setup",
    desktopSetupTitle: "Connect the Desktop app to your JOBolar.Ai account",
    desktopSetupDescription: "When Desktop asks for a connection code, finish these steps in your browser.",
    desktopSetupAccess: "Requires Pro",
    desktopSetupSteps: [
      "Open JOBolar.Ai Desktop and choose Connect.",
      "In your browser, sign in to the JOBolar.Ai account you want to use.",
      "Open Desktop connection settings below and choose Create one-time code.",
      "Copy the code, paste it into the Desktop prompt, then choose OK. The code expires after five minutes and works once.",
    ],
    desktopSetupCta: "Create Desktop connection code",
    desktopSetupSecurity: "After connection, Desktop stores its credential in Windows Credential Manager or macOS Keychain.",
  },
};

export default en;
